<script setup>
import { computed } from 'vue';
import { eachDayOfInterval } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { isToday } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import CalendarEventChip from 'dashboard/components-next/Calendar/CalendarEventChip.vue';

const props = defineProps({
  visibleRange: { type: Object, required: true },
  eventsForDay: { type: Function, required: true },
});

defineEmits(['reschedule', 'complete']);

const { t, locale } = useI18n();

const today = computed(() => new Date());

// Computed once per render instead of calling eventsForDay(day) twice per
// column (list + empty-state check).
const daysWithEvents = computed(() =>
  eachDayOfInterval({
    start: props.visibleRange.start,
    end: props.visibleRange.end,
  }).map(day => ({ day, events: props.eventsForDay(day) }))
);

const dayLabel = day =>
  day.toLocaleDateString(locale.value, { weekday: 'short', day: 'numeric' });
</script>

<template>
  <div
    class="grid flex-1 min-h-0 grid-cols-7 overflow-hidden border rounded-lg border-n-weak"
  >
    <div
      v-for="cell in daysWithEvents"
      :key="cell.day.getTime()"
      class="flex flex-col min-h-0 border-r border-n-weak last:border-r-0"
    >
      <div
        class="flex-shrink-0 px-2 py-2 text-xs font-medium text-center border-b text-n-slate-11 border-n-weak"
        :class="{ 'text-n-brand': isToday(today, cell.day) }"
      >
        {{ dayLabel(cell.day) }}
      </div>
      <div class="flex flex-col flex-1 min-h-0 gap-0.5 p-1.5 overflow-y-auto">
        <CalendarEventChip
          v-for="event in cell.events"
          :key="event.contact.id"
          :event="event"
          @reschedule="$emit('reschedule', $event)"
          @complete="$emit('complete', $event)"
        />
        <span
          v-if="!cell.events.length"
          class="px-1.5 text-[11px] text-n-slate-8"
        >
          {{ t('CALENDAR.NO_EVENTS') }}
        </span>
      </div>
    </div>
  </div>
</template>
